import { useState, useCallback, useRef, useEffect } from 'react';
import { PortfolioItem } from '@/hooks/profile';

const MAX_HISTORY_SIZE = 50;

/**
 * Snapshot of a shot for history tracking
 * Only includes user-editable fields, excludes system fields
 */
export interface ShotSnapshot {
  title: string;
  description: string;
  image_url: string;
  images: Array<{ id: string; url: string; caption?: string }>;
  is_featured: boolean;
  status: 'draft' | 'published';
  timestamp: number;
}

/**
 * History state for undo/redo
 */
export interface HistoryState {
  past: ShotSnapshot[];
  present: ShotSnapshot;
  future: ShotSnapshot[];
}

/**
 * Create a snapshot of the shot for history tracking
 * Only includes user-editable fields, excludes system fields
 */
function createSnapshot(shot: PortfolioItem): ShotSnapshot {
  return {
    title: shot.title || '',
    description: shot.description || '',
    image_url: shot.image_url || '',
    images: JSON.parse(JSON.stringify(shot.images || [])), // Deep clone
    is_featured: shot.is_featured || false,
    status: shot.status || 'draft',
    timestamp: Date.now(),
  };
}

/**
 * Check if two snapshots are equal (excluding timestamp)
 */
function snapshotsEqual(a: ShotSnapshot, b: ShotSnapshot): boolean {
  const aWithoutTimestamp = { ...a, timestamp: 0 };
  const bWithoutTimestamp = { ...b, timestamp: 0 };
  return JSON.stringify(aWithoutTimestamp) === JSON.stringify(bWithoutTimestamp);
}

/**
 * Restore a shot from a snapshot, preserving system fields
 */
function restoreFromSnapshot(
  currentShot: PortfolioItem,
  snapshot: ShotSnapshot
): PortfolioItem {
  return {
    ...currentShot,
    title: snapshot.title,
    description: snapshot.description,
    image_url: snapshot.image_url,
    images: JSON.parse(JSON.stringify(snapshot.images)), // Deep clone
    is_featured: snapshot.is_featured,
    status: snapshot.status,
  };
}

interface UseShotHistoryOptions {
  enabled?: boolean;
  debounceMs?: number;
}

interface UseShotHistoryReturn {
  undo: () => ShotSnapshot | null;
  redo: () => ShotSnapshot | null;
  canUndo: boolean;
  canRedo: boolean;
  currentSnapshot: ShotSnapshot;
  wrapSetState: (
    originalSetState: React.Dispatch<React.SetStateAction<PortfolioItem>>
  ) => (
    update: PortfolioItem | ((prev: PortfolioItem) => PortfolioItem),
    options?: { skipHistory?: boolean; immediate?: boolean }
  ) => void;
  clearHistory: () => void;
}

/**
 * Hook for managing undo/redo history of shot edits
 *
 * Features:
 * - Tracks up to 50 history entries
 * - Debounces rapid changes to prevent history spam
 * - Skips system fields (id, timestamps, slug)
 * - Deep clones images to prevent reference issues
 * - Provides wrapped setState to track changes
 */
export function useShotHistory(
  shot: PortfolioItem,
  options: UseShotHistoryOptions = {}
): UseShotHistoryReturn {
  const { enabled = true, debounceMs = 500 } = options;

  const [history, setHistory] = useState<HistoryState>(() => ({
    past: [],
    present: createSnapshot(shot),
    future: [],
  }));

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isUndoRedoRef = useRef(false);
  const lastProcessedSnapshotRef = useRef<ShotSnapshot>(createSnapshot(shot));

  /**
   * Add a snapshot to history (with debouncing for text changes)
   */
  const addToHistory = useCallback(
    (snapshot: ShotSnapshot, immediate: boolean = false) => {
      if (!enabled || isUndoRedoRef.current) return;

      // Skip if snapshot is identical to last processed
      if (snapshotsEqual(snapshot, lastProcessedSnapshotRef.current)) {
        return;
      }

      const performAdd = () => {
        setHistory((prev) => {
          // Skip if identical to current present
          if (snapshotsEqual(snapshot, prev.present)) {
            return prev;
          }

          lastProcessedSnapshotRef.current = snapshot;

          // Add current present to past, set new present, clear future
          let newPast = [...prev.past, prev.present];

          // Limit history size (FIFO)
          if (newPast.length > MAX_HISTORY_SIZE) {
            newPast = newPast.slice(newPast.length - MAX_HISTORY_SIZE);
          }

          return {
            past: newPast,
            present: snapshot,
            future: [], // Clear redo stack on new change
          };
        });
      };

      if (immediate) {
        // Clear any pending debounced adds
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
          debounceTimerRef.current = null;
        }
        performAdd();
      } else {
        // Debounce for text changes
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(performAdd, debounceMs);
      }
    },
    [enabled, debounceMs]
  );

  /**
   * Undo to previous state
   * Returns the snapshot to restore, or null if no history
   */
  const undo = useCallback((): ShotSnapshot | null => {
    if (history.past.length === 0) return null;

    isUndoRedoRef.current = true;

    let snapshotToRestore: ShotSnapshot | null = null;

    setHistory((prev) => {
      const previous = prev.past[prev.past.length - 1];
      const newPast = prev.past.slice(0, -1);

      snapshotToRestore = previous;

      return {
        past: newPast,
        present: previous,
        future: [prev.present, ...prev.future],
      };
    });

    // Reset flag after state update
    setTimeout(() => {
      isUndoRedoRef.current = false;
    }, 0);

    return snapshotToRestore;
  }, [history.past.length]);

  /**
   * Redo to next state
   * Returns the snapshot to restore, or null if no future
   */
  const redo = useCallback((): ShotSnapshot | null => {
    if (history.future.length === 0) return null;

    isUndoRedoRef.current = true;

    let snapshotToRestore: ShotSnapshot | null = null;

    setHistory((prev) => {
      const next = prev.future[0];
      const newFuture = prev.future.slice(1);

      snapshotToRestore = next;

      return {
        past: [...prev.past, prev.present],
        present: next,
        future: newFuture,
      };
    });

    // Reset flag after state update
    setTimeout(() => {
      isUndoRedoRef.current = false;
    }, 0);

    return snapshotToRestore;
  }, [history.future.length]);

  /**
   * Clear all history
   */
  const clearHistory = useCallback(() => {
    setHistory({
      past: [],
      present: createSnapshot(shot),
      future: [],
    });
    lastProcessedSnapshotRef.current = createSnapshot(shot);
  }, [shot]);

  /**
   * Wrap the original setState to track changes in history
   */
  const wrapSetState = useCallback(
    (originalSetState: React.Dispatch<React.SetStateAction<PortfolioItem>>) => {
      return (
        update: PortfolioItem | ((prev: PortfolioItem) => PortfolioItem),
        options?: { skipHistory?: boolean; immediate?: boolean }
      ) => {
        // Call original setState
        originalSetState((prev) => {
          const nextState = typeof update === 'function' ? update(prev) : update;

          // Track in history unless explicitly skipped or during undo/redo
          if (!options?.skipHistory && !isUndoRedoRef.current && enabled) {
            const snapshot = createSnapshot(nextState);
            addToHistory(snapshot, options?.immediate);
          }

          return nextState;
        });
      };
    },
    [addToHistory, enabled]
  );

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    undo,
    redo,
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
    currentSnapshot: history.present,
    wrapSetState,
    clearHistory,
  };
}

// Export utility functions for testing
export { createSnapshot, snapshotsEqual, restoreFromSnapshot };
